import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSlider } from '@angular/material/slider';
import { IconStatus } from '../../interfaces/list-item.interfaces';

export class SliderChange {
  source: SliderComponent;
  value: number | null;
}

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SliderComponent
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent implements ControlValueAccessor {
  // Used to prevent things like step, min and max from being too small for logarithmic displays
  private static readonly MIN_LOG_RANGE_VALUE: number = 0.001;

  @Input() public leftIcon: IconStatus = { class: '', icon: 'remove' };
  @Input() public rightIcon: IconStatus = { class: '', icon: 'add' };
  @Input() public min: number = 0;
  @Input() public max: number = 100;
  @Input() public step: number = 1;
  @Input() public logarithmic: boolean = false;
  @Input() public disabled: boolean = false;

  @Input() public value: number = 0;

  @Output() readonly change: EventEmitter<SliderChange> = new EventEmitter<SliderChange>();
  @Output() readonly input: EventEmitter<SliderChange> = new EventEmitter<SliderChange>();

  @ViewChild('slider') private matSlider: MatSlider;

  private onTouched: any = null;
  private onChange: any = null;
  private touched: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
  ) { }

  public writeValue(obj: any): void {
    this.value = Number(obj);
    this.input.emit(this.createChangeEvent());
    this.change.emit(this.createChangeEvent());
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.matSlider.disabled = isDisabled
  }

  public onSliderInput(newValue: number | string): void {
    this.value = Number(newValue);
    this.markAsTouched();
    this.input.emit(this.createChangeEvent());
    this.cdr.markForCheck();
  }

  public onSliderChange(newValue: number | string): void {
    this.value = Number(newValue);
    if (this.onChange) {
      this.onChange(this.value);
    }
    this.change.emit(this.createChangeEvent());
    this.cdr.markForCheck();
  }

  public onIconClick(nrOfSteps: number): void {
    if (this.disabled) {
      return;
    }

    const delta: number = nrOfSteps * this.step;
    if((this.value + delta) >= this.min && (this.value + delta) <= this.max){
      this.onSliderChange(+(this.value + delta).toFixed(2));
    }
  }

  public internalToExternalValue(internal: number | string): number {
    if (typeof internal === 'string') {
      internal = Number(internal);
    }
    if (this.logarithmic) {
      return Math.round(Math.pow(10, internal));
    }
    return internal;
  }

  public externalToInternalValue(external: number | string): number {
    if (typeof external === 'string') {
      external = Number(external);
    }
    if (this.logarithmic) {
      return Math.log10(external);
    }
    return external;
  }

  public externalToInternalValueClamped(external: number | string): number {
    if (typeof external === 'string') {
      external = Number(external);
    }
    if (this.logarithmic) {
      let internal: number = this.externalToInternalValue(external);
      if (internal < SliderComponent.MIN_LOG_RANGE_VALUE) {
        internal = SliderComponent.MIN_LOG_RANGE_VALUE;
      }
      return internal;
    }
    return external;
  }

  private markAsTouched(): void {
    if (!this.touched) {
      if (this.onTouched) {
        this.onTouched();
      }
      this.touched = true;
    }
  }

  private createChangeEvent(): SliderChange {
    return {
      source: this,
      value: this.value
    };
  }
}
